// ABOUTME: Home product carousel behaviors: arrow paging over the native scroll-snap track, and
// ABOUTME: In-card flavor panels use the native variant and cart pipeline, with resolved images and prices.

import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

class JfPpCarousel extends HTMLElement {
  #controller;
  #openCard;
  #selections = new WeakMap();

  connectedCallback() {
    this.track = this.querySelector('.ppc-track');
    this.prev = this.querySelector('.ppc-arrow--prev');
    this.next = this.querySelector('.ppc-arrow--next');
    if (!this.track) return;
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.prev?.addEventListener('click', () => this.#page(-1), { signal });
    this.next?.addEventListener('click', () => this.#page(1), { signal });
    this.track.addEventListener('scroll', this.#sync, { passive: true, signal });
    window.addEventListener('resize', this.#sync, { passive: true, signal });
    this.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.#openCard) return;
      event.preventDefault();
      this.#closePanel(true);
    }, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (this.#openCard && !this.#openCard.contains(event.target)) this.#closePanel();
    }, { signal });
    for (const card of this.querySelectorAll('.ppc-card')) this.#preparePicker(card);
    this.#sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#closePanel();
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const open = event.target.closest('[data-ppc-open]');
    if (open) {
      const card = open.closest('.ppc-card');
      const panel = card?.querySelector('.ppc-panel');
      if (!panel) return;
      this.#closePanel();
      this.#openCard = card;
      this.#preparePicker(card);
      panel.inert = false;
      panel.setAttribute('aria-hidden', 'false');
      open.setAttribute('aria-expanded', 'true');
      card.classList.add('ppc-card--open');
      panel.querySelector('[data-ppc-close]')?.focus({ preventScroll: true });
    } else if (event.target.closest('[data-ppc-close]')) {
      this.#closePanel(true);
    }
  };

  #closePanel(restoreFocus = false) {
    const card = this.#openCard;
    if (!card) return;
    const trigger = card.querySelector('[data-ppc-open]');
    if (restoreFocus) trigger?.focus({ preventScroll: true });
    trigger?.setAttribute('aria-expanded', 'false');
    card.classList.remove('ppc-card--open');
    const panel = card.querySelector('.ppc-panel');
    if (panel) {
      panel.inert = true;
      panel.setAttribute('aria-hidden', 'true');
    }
    this.#openCard = null;
  }

  #preparePicker(card) {
    // The panel shows every flavor in a wrapping list; native picker inputs retain their bindings.
    const overflow = card.querySelector('.ppc-panel overflow-list');
    if (overflow) {
      const list = document.createElement('ul');
      list.className = 'ppc-swatches';
      for (const option of overflow.querySelectorAll(':scope > li:not([slot="more"])')) {
        option.removeAttribute('slot');
        list.append(option);
      }
      overflow.replaceWith(list);
    }
    const fieldset = card.querySelector('.ppc-panel fieldset');
    if (fieldset) fieldset.setAttribute('aria-label', card.querySelector('.ppc-panel')?.dataset.ppcOption || 'Flavor');
  }

  #page(direction) {
    this.#closePanel();
    const card = this.track.querySelector('.ppc-card');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth / 2;
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    this.track.scrollBy({ left: direction * step, behavior });
  }

  #sync = () => {
    const max = this.track.scrollWidth - this.track.clientWidth - 1;
    this.prev?.toggleAttribute('hidden', this.track.scrollLeft <= 0);
    this.next?.toggleAttribute('hidden', this.track.scrollLeft >= max);
    this.style.setProperty('--ppc-arrow-top', `${this.track.offsetTop + this.track.clientHeight / 2}px`);
  };

  /**
   * The card's swatch picker resolves the pick server-side (product-card.js re-points the
   * links, product-form.js re-points the hidden variant id); the resolved variant carries the
   * price this card shows, formatted like the server render.
   * @param {CustomEvent & { promise?: Promise<{ detail?: { resource?: any } }> }} event
   */
  #onProductSelect = (event) => {
    const card = event.target instanceof Element ? event.target.closest('product-card') : null;
    if (!card || !event.promise) return;
    this.#selections.set(card, event.promise);
    const selected = card.querySelector('input[type="radio"]:checked');
    const selectedImage = selected?.dataset.optionImageUrl;
    const selectedName = selected?.getAttribute('aria-label');
    const buy = card.querySelector('button[type="submit"].ppc-buy');
    if (buy) buy.disabled = true;
    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || this.#selections.get(card) !== event.promise) return;
        this.#preparePicker(card);
        const resource = detail?.resource;
        if (!resource || typeof resource.price !== 'number') {
          if (buy) setLabel(buy, buy.dataset.soldOutLabel ?? '');
          return;
        }
        this.#reprice(card, resource);
        const image = card.querySelector('.ppc-panel-image');
        const imageUrl = resource.featured_image?.src || selectedImage;
        if (image && imageUrl) {
          image.src = imageUrl;
          image.removeAttribute('srcset');
          image.alt = selectedName || resource.featured_image?.alt || resource.title || '';
        }
        const link = card.querySelector('.ppc-panel-details');
        const productLink = card.querySelector('[ref="productCardLink"]');
        if (link && productLink) link.href = productLink.href;
      })
      .catch(() => {
        if (this.#selections.get(card) !== event.promise) return;
        if (buy) buy.disabled = false;
        const status = card.querySelector('[ref="addToCartTextError"]');
        if (status) {
          status.classList.remove('hidden');
          const message = 'Unable to load this flavor. Please choose it again.';
          const textNode = status.childNodes[2];
          if (textNode) {
            textNode.textContent = message;
          } else {
            status.appendChild(document.createTextNode(message));
          }
        }
      });
  };

  /**
   * @param {Element} card
   * @param {{ price: number, compare_at_price?: number | null, available?: boolean }} resource
   */
  #reprice(card, resource) {
    const value = card.querySelector('.ppc-card-price-value');
    const priceRow = card.querySelector('.ppc-card-price');
    const pill = card.querySelector('button[type="submit"].ppc-buy');
    const price = this.#money(resource.price);

    const status = card.querySelector('[ref="addToCartTextError"]');
    if (status) status.classList.add('hidden');

    if (pill instanceof HTMLButtonElement) {
      const available = resource.available !== false;
      pill.disabled = !available;
      setLabel(pill, available
        ? (pill.dataset.buyTemplate ?? '{price}').replace('{price}', price)
        : pill.dataset.soldOutLabel ?? '');
    }

    if (!value || !priceRow) return;

    value.textContent = price;
    const trigger = card.querySelector('[data-ppc-open]');
    if (trigger) trigger.textContent = (trigger.dataset.buyTemplate ?? '{price}').replace('{price}', price);

    const compareCents = resource.compare_at_price ?? 0;
    const onSale = compareCents > resource.price;
    let compare = priceRow.querySelector('.ppc-card-compare');
    if (onSale) {
      if (!compare) {
        compare = document.createElement('s');
        compare.className = 'ppc-card-compare';
        priceRow.append(compare);
      }
      compare.textContent = this.#money(compareCents);
    } else {
      compare?.remove();
    }
    priceRow.classList.toggle('ppc-card-price--sale', onSale);
  }

  /**
   * Formats a price in cents with the store's money format and no zero cents ("$69", "$44.99"),
   * so a repriced card and buy pill read exactly like the `money_without_trailing_zeros` filter's
   * server render.
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD').replace(/\.00(?=\D*$)/, '');
  }
}

/**
 * Writes a buy pill's label into its text span so the checkmark-burst markup beside it survives.
 * @param {HTMLButtonElement} button
 * @param {string} text
 */
function setLabel(button, text) {
  (button.querySelector('.add-to-cart-text') ?? button).textContent = text;
}

if (!customElements.get('jf-pp-carousel')) {
  customElements.define('jf-pp-carousel', JfPpCarousel);
}
